// ==================== LANDING DESIGN SYSTEM ====================
// Premium templates, color schemes, and typography for landing pages

// ==================== PREMIUM COLOR SCHEMES ====================
export const COLOR_SCHEMES = {
  // Modern & Professional
  midnight: {
    id: 'midnight',
    name: 'Midnight Pro',
    description: 'Elegante y profesional',
    primary: '#6366F1',
    secondary: '#8B5CF6', 
    accent: '#F59E0B',
    background: '#0F0F1A',
    surface: '#1A1A2E',
    text: '#F8FAFC',
    textMuted: '#94A3B8',
    gradient: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%)',
    heroGradient: 'linear-gradient(180deg, #0F0F1A 0%, #1E1E3F 50%, #0F0F1A 100%)'
  },
  sunset: {
    id: 'sunset',
    name: 'Sunset Warm',
    description: 'Cálido y energético',
    primary: '#F97316',
    secondary: '#FB923C',
    accent: '#FBBF24',
    background: '#1C1917',
    surface: '#292524',
    text: '#FAFAF9',
    textMuted: '#A8A29E',
    gradient: 'linear-gradient(135deg, #F97316 0%, #FB923C 50%, #FBBF24 100%)',
    heroGradient: 'linear-gradient(180deg, #1C1917 0%, #292524 100%)'
  },
  ocean: {
    id: 'ocean',
    name: 'Ocean Deep',
    description: 'Confianza y calma',
    primary: '#0EA5E9',
    secondary: '#06B6D4',
    accent: '#14B8A6',
    background: '#0C1222',
    surface: '#162032',
    text: '#F0F9FF',
    textMuted: '#7DD3FC',
    gradient: 'linear-gradient(135deg, #0EA5E9 0%, #06B6D4 50%, #14B8A6 100%)',
    heroGradient: 'linear-gradient(180deg, #0C1222 0%, #0F172A 50%, #0C1222 100%)'
  },
  forest: {
    id: 'forest',
    name: 'Forest Growth',
    description: 'Natural y próspero',
    primary: '#10B981',
    secondary: '#34D399',
    accent: '#A3E635',
    background: '#0A1612',
    surface: '#132B23',
    text: '#ECFDF5',
    textMuted: '#6EE7B7',
    gradient: 'linear-gradient(135deg, #10B981 0%, #34D399 50%, #A3E635 100%)',
    heroGradient: 'linear-gradient(180deg, #0A1612 0%, #132B23 100%)'
  },
  ruby: {
    id: 'ruby',
    name: 'Ruby Passion',
    description: 'Impactante y audaz',
    primary: '#E11D48',
    secondary: '#F43F5E',
    accent: '#FB7185',
    background: '#1A0A10',
    surface: '#2D1420',
    text: '#FFF1F2',
    textMuted: '#FDA4AF',
    gradient: 'linear-gradient(135deg, #E11D48 0%, #F43F5E 50%, #FB7185 100%)',
    heroGradient: 'linear-gradient(180deg, #1A0A10 0%, #2D1420 100%)'
  },
  // Light themes
  clean: {
    id: 'clean',
    name: 'Clean White',
    description: 'Minimalista y limpio',
    primary: '#3B82F6',
    secondary: '#6366F1',
    accent: '#8B5CF6',
    background: '#FFFFFF',
    surface: '#F8FAFC',
    text: '#0F172A',
    textMuted: '#64748B',
    gradient: 'linear-gradient(135deg, #3B82F6 0%, #6366F1 50%, #8B5CF6 100%)',
    heroGradient: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 50%, #F8FAFC 100%)'
  },
  cream: {
    id: 'cream',
    name: 'Cream Luxury',
    description: 'Premium y elegante',
    primary: '#CA8A04',
    secondary: '#D97706',
    accent: '#B45309',
    background: '#FFFBEB',
    surface: '#FEF3C7',
    text: '#1C1917',
    textMuted: '#78716C',
    gradient: 'linear-gradient(135deg, #CA8A04 0%, #D97706 50%, #B45309 100%)',
    heroGradient: 'linear-gradient(180deg, #FFFBEB 0%, #FEF3C7 100%)'
  }
};

// ==================== TYPOGRAPHY PRESETS ====================
export const TYPOGRAPHY_PRESETS = {
  modern: {
    id: 'modern',
    name: 'Modern Sans',
    headingFont: "'Inter', sans-serif",
    bodyFont: "'Inter', sans-serif",
    headingWeight: '800',
    bodyWeight: '400',
    letterSpacing: '-0.02em',
    lineHeight: '1.2'
  },
  elegant: {
    id: 'elegant', 
    name: 'Elegant Serif',
    headingFont: "'Playfair Display', serif",
    bodyFont: "'Source Sans Pro', sans-serif",
    headingWeight: '700',
    bodyWeight: '400',
    letterSpacing: '0',
    lineHeight: '1.3'
  },
  bold: {
    id: 'bold',
    name: 'Bold Impact',
    headingFont: "'Bebas Neue', sans-serif",
    bodyFont: "'Open Sans', sans-serif",
    headingWeight: '400',
    bodyWeight: '400',
    letterSpacing: '0.05em',
    lineHeight: '1.1'
  },
  tech: {
    id: 'tech',
    name: 'Tech Forward',
    headingFont: "'Space Grotesk', sans-serif",
    bodyFont: "'IBM Plex Sans', sans-serif",
    headingWeight: '700',
    bodyWeight: '400',
    letterSpacing: '-0.01em',
    lineHeight: '1.25'
  },
  friendly: {
    id: 'friendly',
    name: 'Friendly Rounded',
    headingFont: "'Nunito', sans-serif",
    bodyFont: "'Nunito', sans-serif",
    headingWeight: '800',
    bodyWeight: '400',
    letterSpacing: '0',
    lineHeight: '1.4'
  }
};

// ==================== PREMIUM LANDING TEMPLATES ====================
export const LANDING_TEMPLATES = {
  // Template 1: High-Converting Sales Page
  salesPro: {
    id: 'salesPro',
    name: 'Sales Pro',
    description: 'Alta conversión para cursos y servicios',
    thumbnail: '/templates/sales-pro.jpg',
    colorScheme: 'midnight',
    typography: 'modern',
    sections: [
      {
        type: 'hero',
        styles: {
          background: 'gradient',
          layout: 'split', // image on right
          padding: 'large',
          animation: 'fadeUp'
        }
      },
      {
        type: 'pain_points',
        styles: {
          background: 'dark',
          layout: 'cards',
          iconStyle: 'emoji'
        }
      },
      {
        type: 'solution',
        styles: {
          background: 'gradient',
          layout: 'centered'
        }
      },
      {
        type: 'benefits',
        styles: {
          background: 'light',
          layout: 'grid-3',
          iconStyle: 'gradient-circle'
        }
      },
      {
        type: 'testimonials',
        styles: {
          background: 'dark',
          layout: 'carousel',
          showAvatar: true,
          showRating: true
        }
      },
      {
        type: 'offer',
        styles: {
          background: 'gradient',
          layout: 'pricing-table'
        }
      },
      {
        type: 'faq',
        styles: {
          background: 'light',
          layout: 'accordion'
        }
      },
      {
        type: 'final_cta',
        styles: {
          background: 'gradient',
          urgency: true
        }
      }
    ]
  },

  // Template 2: Webinar / Live Room
  webinarPro: {
    id: 'webinarPro',
    name: 'Webinar Pro',
    description: 'Perfecto para directos y webinars',
    thumbnail: '/templates/webinar-pro.jpg',
    colorScheme: 'ocean',
    typography: 'tech',
    sections: [
      {
        type: 'hero',
        styles: {
          background: 'gradient',
          layout: 'centered',
          showCountdown: true,
          showDate: true
        }
      },
      {
        type: 'host_intro',
        styles: {
          background: 'light',
          layout: 'photo-left'
        }
      },
      {
        type: 'agenda',
        styles: {
          background: 'dark',
          layout: 'timeline'
        }
      },
      {
        type: 'benefits',
        styles: {
          background: 'light',
          layout: 'checklist'
        }
      },
      {
        type: 'live_room',
        styles: {
          background: 'gradient'
        }
      },
      {
        type: 'social_proof',
        styles: {
          background: 'dark',
          layout: 'logos'
        }
      }
    ]
  },

  // Template 3: Lead Magnet
  leadMagnet: {
    id: 'leadMagnet',
    name: 'Lead Magnet',
    description: 'Captura leads con un recurso gratuito',
    thumbnail: '/templates/lead-magnet.jpg',
    colorScheme: 'forest',
    typography: 'friendly',
    sections: [
      {
        type: 'hero',
        styles: {
          background: 'gradient',
          layout: 'form-right',
          formStyle: 'floating'
        }
      },
      {
        type: 'preview',
        styles: {
          background: 'light',
          layout: 'mockup'
        }
      },
      {
        type: 'benefits',
        styles: {
          background: 'dark',
          layout: 'icon-list'
        }
      },
      {
        type: 'author',
        styles: {
          background: 'light',
          layout: 'card'
        }
      },
      {
        type: 'final_cta',
        styles: {
          background: 'gradient'
        }
      }
    ]
  },

  // Template 4: Luxury / High Ticket
  luxury: {
    id: 'luxury',
    name: 'Luxury',
    description: 'Para servicios premium y high ticket',
    thumbnail: '/templates/luxury.jpg',
    colorScheme: 'cream',
    typography: 'elegant',
    sections: [
      {
        type: 'hero',
        styles: {
          background: 'image',
          layout: 'fullscreen',
          overlay: true,
          textAlign: 'center'
        }
      },
      {
        type: 'intro',
        styles: {
          background: 'light',
          layout: 'narrow'
        }
      },
      {
        type: 'gallery',
        styles: {
          background: 'dark',
          layout: 'masonry'
        }
      },
      {
        type: 'testimonials',
        styles: {
          background: 'light',
          layout: 'quote-large'
        }
      },
      {
        type: 'process',
        styles: {
          background: 'dark',
          layout: 'steps'
        }
      },
      {
        type: 'contact',
        styles: {
          background: 'gradient',
          layout: 'application-form'
        }
      }
    ]
  },

  // Template 5: Product Launch
  launch: {
    id: 'launch',
    name: 'Product Launch',
    description: 'Lanzamiento de productos digitales',
    thumbnail: '/templates/launch.jpg',
    colorScheme: 'ruby',
    typography: 'bold',
    sections: [
      {
        type: 'hero',
        styles: {
          background: 'video',
          layout: 'centered',
          countdownStyle: 'flip'
        }
      },
      {
        type: 'problem_agitation',
        styles: {
          background: 'dark',
          layout: 'story'
        }
      },
      {
        type: 'reveal',
        styles: {
          background: 'gradient',
          animation: 'dramatic'
        }
      },
      {
        type: 'features',
        styles: {
          background: 'light',
          layout: 'bento-grid'
        }
      },
      {
        type: 'bonuses',
        styles: {
          background: 'dark',
          layout: 'stack'
        }
      },
      {
        type: 'pricing',
        styles: {
          background: 'gradient',
          layout: 'comparison'
        }
      },
      {
        type: 'guarantee',
        styles: {
          background: 'light',
          badgeStyle: 'seal'
        }
      },
      {
        type: 'final_cta',
        styles: {
          background: 'gradient',
          urgency: true,
          scarcity: true
        }
      }
    ]
  },

  // Template 6: VSL / Video Sales Letter
  vsl: {
    id: 'vsl',
    name: 'VSL Funnel',
    description: 'Video de ventas con alta conversión',
    thumbnail: '/templates/vsl.jpg',
    colorScheme: 'midnight',
    typography: 'modern',
    sections: [
      {
        type: 'hero',
        styles: {
          background: 'dark',
          layout: 'video-centered',
          showVideo: true
        }
      },
      {
        type: 'video_player',
        styles: {
          background: 'dark',
          size: 'large'
        }
      },
      {
        type: 'cta',
        styles: {
          background: 'gradient',
          hidden_until_video_ends: true
        }
      },
      {
        type: 'testimonials',
        styles: {
          background: 'dark',
          layout: 'carousel'
        }
      },
      {
        type: 'faq',
        styles: {
          background: 'light'
        }
      }
    ]
  },

  // Template 7: Coaching / Consulting
  coaching: {
    id: 'coaching',
    name: 'Coaching Pro',
    description: 'Para coaches y consultores',
    thumbnail: '/templates/coaching.jpg',
    colorScheme: 'ocean',
    typography: 'friendly',
    sections: [
      {
        type: 'hero',
        styles: {
          background: 'gradient',
          layout: 'centered',
          showExpertPhoto: true
        }
      },
      {
        type: 'story',
        styles: {
          background: 'light',
          layout: 'narrative'
        }
      },
      {
        type: 'method',
        styles: {
          background: 'dark',
          layout: 'steps-3'
        }
      },
      {
        type: 'results',
        styles: {
          background: 'light',
          layout: 'before-after'
        }
      },
      {
        type: 'testimonials',
        styles: {
          background: 'gradient',
          layout: 'video-testimonials'
        }
      },
      {
        type: 'pricing',
        styles: {
          background: 'light',
          layout: 'single-offer'
        }
      },
      {
        type: 'final_cta',
        styles: {
          background: 'gradient'
        }
      }
    ]
  },

  // Template 8: E-commerce / Product
  ecommerce: {
    id: 'ecommerce',
    name: 'Product Page',
    description: 'Ideal para productos físicos o digitales',
    thumbnail: '/templates/ecommerce.jpg',
    colorScheme: 'clean',
    typography: 'modern',
    sections: [
      {
        type: 'hero',
        styles: {
          background: 'light',
          layout: 'product-gallery',
          showPrice: true
        }
      },
      {
        type: 'features',
        styles: {
          background: 'white',
          layout: 'specs-table'
        }
      },
      {
        type: 'benefits',
        styles: {
          background: 'light',
          layout: 'icon-grid'
        }
      },
      {
        type: 'reviews',
        styles: {
          background: 'white',
          layout: 'star-ratings'
        }
      },
      {
        type: 'comparison',
        styles: {
          background: 'light',
          layout: 'vs-table'
        }
      },
      {
        type: 'cta',
        styles: {
          background: 'brand',
          sticky: true
        }
      }
    ]
  },

  // Template 9: Free Training / Challenge
  challenge: {
    id: 'challenge',
    name: 'Free Challenge',
    description: 'Reto o entrenamiento gratuito',
    thumbnail: '/templates/challenge.jpg',
    colorScheme: 'forest',
    typography: 'bold',
    sections: [
      {
        type: 'hero',
        styles: {
          background: 'gradient',
          layout: 'centered',
          showCountdown: true,
          showSpots: true
        }
      },
      {
        type: 'what_youll_learn',
        styles: {
          background: 'light',
          layout: 'day-by-day'
        }
      },
      {
        type: 'host',
        styles: {
          background: 'dark',
          layout: 'bio-card'
        }
      },
      {
        type: 'bonuses',
        styles: {
          background: 'gradient',
          layout: 'bonus-stack'
        }
      },
      {
        type: 'testimonials',
        styles: {
          background: 'light',
          layout: 'grid'
        }
      },
      {
        type: 'registration',
        styles: {
          background: 'gradient',
          formStyle: 'floating'
        }
      }
    ]
  },

  // Template 10: Agency / Services
  agency: {
    id: 'agency',
    name: 'Agency Pro',
    description: 'Para agencias y servicios B2B',
    thumbnail: '/templates/agency.jpg',
    colorScheme: 'midnight',
    typography: 'tech',
    sections: [
      {
        type: 'hero',
        styles: {
          background: 'dark',
          layout: 'centered',
          showLogos: true
        }
      },
      {
        type: 'services',
        styles: {
          background: 'light',
          layout: 'service-cards'
        }
      },
      {
        type: 'case_studies',
        styles: {
          background: 'dark',
          layout: 'portfolio'
        }
      },
      {
        type: 'process',
        styles: {
          background: 'light',
          layout: 'timeline'
        }
      },
      {
        type: 'team',
        styles: {
          background: 'dark',
          layout: 'team-grid'
        }
      },
      {
        type: 'contact',
        styles: {
          background: 'gradient',
          layout: 'split-form'
        }
      }
    ]
  }
};

// ==================== SECTION STYLE VARIANTS ====================
export const SECTION_VARIANTS = {
  hero: {
    centered: {
      name: 'Centrado',
      layout: 'flex flex-col items-center text-center',
      headlineClass: 'text-4xl md:text-6xl lg:text-7xl font-extrabold max-w-4xl mx-auto',
      subheadlineClass: 'text-xl md:text-2xl max-w-2xl mx-auto mt-6 opacity-90'
    },
    split: {
      name: 'Dividido',
      layout: 'grid md:grid-cols-2 gap-12 items-center',
      headlineClass: 'text-4xl md:text-5xl lg:text-6xl font-extrabold',
      subheadlineClass: 'text-lg md:text-xl mt-6 opacity-90'
    },
    fullscreen: {
      name: 'Pantalla Completa',
      layout: 'min-h-screen flex flex-col items-center justify-center text-center',
      headlineClass: 'text-5xl md:text-7xl lg:text-8xl font-black max-w-5xl',
      subheadlineClass: 'text-2xl md:text-3xl max-w-3xl mx-auto mt-8 opacity-90'
    }
  },
  benefits: {
    'grid-3': {
      name: '3 Columnas',
      containerClass: 'grid md:grid-cols-3 gap-8',
      cardClass: 'p-6 rounded-2xl'
    },
    'grid-2': {
      name: '2 Columnas',
      containerClass: 'grid md:grid-cols-2 gap-8',
      cardClass: 'p-8 rounded-2xl'
    },
    checklist: {
      name: 'Lista',
      containerClass: 'max-w-2xl mx-auto space-y-4',
      cardClass: 'flex items-start gap-4 p-4'
    },
    'icon-list': {
      name: 'Con Iconos',
      containerClass: 'max-w-3xl mx-auto space-y-6',
      cardClass: 'flex items-start gap-6 p-6 rounded-xl'
    }
  },
  testimonials: {
    cards: {
      name: 'Tarjetas',
      containerClass: 'grid md:grid-cols-2 lg:grid-cols-3 gap-6',
      cardClass: 'p-6 rounded-2xl'
    },
    carousel: {
      name: 'Carrusel',
      containerClass: 'overflow-hidden',
      cardClass: 'p-8 rounded-2xl'
    },
    'quote-large': {
      name: 'Cita Grande',
      containerClass: 'max-w-4xl mx-auto',
      cardClass: 'text-center p-12'
    }
  }
};

// ==================== ANIMATION PRESETS ====================
export const ANIMATIONS = {
  fadeUp: {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  },
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.5 }
  },
  slideInLeft: {
    initial: { opacity: 0, x: -50 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.6 }
  },
  slideInRight: {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.6 }
  },
  scaleUp: {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.5 }
  },
  stagger: {
    container: {
      animate: { transition: { staggerChildren: 0.1 } }
    },
    item: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 }
    }
  }
};

// ==================== BUTTON STYLES ====================
export const BUTTON_STYLES = {
  solid: {
    name: 'Sólido',
    class: 'px-8 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105 hover:shadow-xl'
  },
  gradient: {
    name: 'Degradado',
    class: 'px-8 py-4 rounded-lg font-bold text-lg bg-gradient-to-r transition-all hover:scale-105 hover:shadow-xl'
  },
  outline: {
    name: 'Borde',
    class: 'px-8 py-4 rounded-lg font-bold text-lg border-2 transition-all hover:scale-105'
  },
  pill: {
    name: 'Píldora',
    class: 'px-10 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 hover:shadow-xl'
  },
  '3d': {
    name: '3D',
    class: 'px-8 py-4 rounded-lg font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all border-b-4'
  }
};

// ==================== ICON STYLES ====================
export const ICON_STYLES = {
  circle: {
    name: 'Círculo',
    wrapper: 'w-12 h-12 rounded-full flex items-center justify-center',
    icon: 'w-6 h-6'
  },
  'gradient-circle': {
    name: 'Círculo Degradado',
    wrapper: 'w-14 h-14 rounded-full flex items-center justify-center bg-gradient-to-br shadow-lg',
    icon: 'w-7 h-7 text-white'
  },
  square: {
    name: 'Cuadrado',
    wrapper: 'w-12 h-12 rounded-xl flex items-center justify-center',
    icon: 'w-6 h-6'
  },
  minimal: {
    name: 'Minimal',
    wrapper: 'w-10 h-10 flex items-center justify-center',
    icon: 'w-8 h-8'
  },
  emoji: {
    name: 'Emoji',
    wrapper: 'text-4xl',
    icon: ''
  }
};

// ==================== SPACING SYSTEM ====================
export const SPACING = {
  section: {
    small: 'py-12 md:py-16',
    medium: 'py-16 md:py-24',
    large: 'py-24 md:py-32',
    xlarge: 'py-32 md:py-48'
  },
  container: {
    narrow: 'max-w-3xl mx-auto px-4',
    normal: 'max-w-5xl mx-auto px-4',
    wide: 'max-w-7xl mx-auto px-4',
    full: 'w-full px-4 md:px-8'
  }
};

// ==================== HELPER: Get Google Fonts URL ====================
export const getGoogleFontsUrl = (typography) => {
  const fonts = {
    modern: 'Inter:wght@400;500;600;700;800',
    elegant: 'Playfair+Display:wght@400;500;600;700|Source+Sans+Pro:wght@400;600',
    bold: 'Bebas+Neue|Open+Sans:wght@400;600',
    tech: 'Space+Grotesk:wght@400;500;600;700|IBM+Plex+Sans:wght@400;500',
    friendly: 'Nunito:wght@400;600;700;800'
  };
  
  return `https://fonts.googleapis.com/css2?family=${fonts[typography] || fonts.modern}&display=swap`;
};

// ==================== HELPER: Generate CSS Variables ====================
export const generateCSSVariables = (colorScheme, typography) => {
  const colors = COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.midnight;
  const typo = TYPOGRAPHY_PRESETS[typography] || TYPOGRAPHY_PRESETS.modern;
  
  return `
    :root {
      --color-primary: ${colors.primary};
      --color-secondary: ${colors.secondary};
      --color-accent: ${colors.accent};
      --color-background: ${colors.background};
      --color-surface: ${colors.surface};
      --color-text: ${colors.text};
      --color-text-muted: ${colors.textMuted};
      --gradient-primary: ${colors.gradient};
      --gradient-hero: ${colors.heroGradient};
      --font-heading: ${typo.headingFont};
      --font-body: ${typo.bodyFont};
      --font-weight-heading: ${typo.headingWeight};
      --font-weight-body: ${typo.bodyWeight};
      --letter-spacing: ${typo.letterSpacing};
      --line-height: ${typo.lineHeight};
    }
  `;
};
